import React, { useState, useCallback, useRef, useEffect } from "react";
import styles from "./ZoomableWrapper.module.css";

interface ZoomableWrapperProps {
  children: React.ReactNode;
  enabled: boolean;
  /** Show zoom controls toolbar */
  showControls?: boolean;
  /** Min zoom level */
  minZoom?: number;
  /** Max zoom level */
  maxZoom?: number;
  /** Zoom step for buttons */
  step?: number;
}

const ZoomableWrapper: React.FC<ZoomableWrapperProps> = ({
  children,
  enabled,
  showControls = true,
  minZoom = 0.25,
  maxZoom = 5,
  step = 0.25,
}) => {
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const lastPosRef = useRef({ x: 0, y: 0 });
  const containerRef = useRef<HTMLDivElement>(null);

  const clampZoom = (z: number) => Math.min(Math.max(z, minZoom), maxZoom);

  const zoomIn = useCallback(() => setZoom((z) => clampZoom(z + step)), [step]);
  const zoomOut = useCallback(
    () => setZoom((z) => clampZoom(z - step)),
    [step],
  );
  const resetView = useCallback(() => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  }, []);

  // ── Wheel zoom (needs non-passive listener to preventDefault) ──
  useEffect(() => {
    const el = containerRef.current;
    if (!el || !enabled) return;

    const handleWheel = (e: WheelEvent) => {
      // Ctrl/Cmd + scroll = zoom
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        e.stopPropagation();
        const delta = e.deltaY > 0 ? -0.1 : 0.1;
        setZoom((z) => clampZoom(z + delta));
        return;
      }
      // When zoomed in, allow panning with plain scroll
      if (zoom > 1) {
        e.preventDefault();
        setPan((p) => ({
          x: p.x - e.deltaX,
          y: p.y - e.deltaY,
        }));
      }
    };

    el.addEventListener("wheel", handleWheel, { passive: false });
    return () => el.removeEventListener("wheel", handleWheel);
  }, [enabled, zoom]);

  // ── Mouse pan ──
  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (zoom <= 1) return;
      // Only left button
      if (e.button !== 0) return;
      setIsPanning(true);
      lastPosRef.current = { x: e.clientX, y: e.clientY };
      e.preventDefault();
    },
    [zoom],
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (!isPanning) return;
      const dx = e.clientX - lastPosRef.current.x;
      const dy = e.clientY - lastPosRef.current.y;
      lastPosRef.current = { x: e.clientX, y: e.clientY };
      setPan((p) => ({ x: p.x + dx, y: p.y + dy }));
    },
    [isPanning],
  );

  const handleMouseUp = useCallback(() => setIsPanning(false), []);

  // Reset when children change significantly (new render)
  useEffect(() => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  }, [children]);

  if (!enabled) return <>{children}</>;

  const isZoomed = zoom !== 1;
  const pctLabel = `${Math.round(zoom * 100)}%`;

  return (
    <div className={styles.zoomableContainer}>
      {/* Zoom controls */}
      {showControls && (
        <div className={styles.zoomControls}>
          <button
            onClick={zoomOut}
            className={styles.zoomBtn}
            title="Zoom out"
            disabled={zoom <= minZoom}
          >
            −
          </button>
          <span
            className={styles.zoomLevel}
            onClick={resetView}
            title="Reset zoom"
          >
            {pctLabel}
          </span>
          <button
            onClick={zoomIn}
            className={styles.zoomBtn}
            title="Zoom in"
            disabled={zoom >= maxZoom}
          >
            +
          </button>
          {isZoomed && (
            <button
              onClick={resetView}
              className={styles.zoomBtn}
              title="Reset view"
            >
              ↺
            </button>
          )}
        </div>
      )}

      {/* Zoomable viewport */}
      <div
        ref={containerRef}
        className={styles.zoomViewport}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        style={{
          cursor: isZoomed
            ? isPanning
              ? "grabbing"
              : "grab"
            : "default",
        }}
      >
        <div
          className={styles.zoomContent}
          style={{
            transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
            transition: isPanning ? "none" : "transform 0.15s ease-out",
          }}
        >
          {children}
        </div>
      </div>
    </div>
  );
};

export default ZoomableWrapper;